import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Button, Spinner, Alert, Card, Badge, Toast, ToastContainer } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { formatDistanceToNow } from 'date-fns';
import { ar } from 'date-fns/locale';
import { 
  FaShareAlt, 
  FaCopy, 
  FaEye, 
  FaArrowLeft,
  FaCalendarAlt,
  FaUser,
  FaTag,
  FaImage,
  FaFileAlt,
  FaStar,
  FaImages,
  FaFile,
  FaVideo,
  FaMusic,
  FaNewspaper,
  FaChartBar,
  FaBullhorn,
  FaPenAlt,
  FaPaperclip,
  FaShareSquare,
  FaExternalLinkAlt,
  FaPlay,
  FaHeadphones,
  FaBookOpen,
  FaExclamationCircle,
  FaHeart,
  FaRegHeart,
  FaComment,
  FaBars,
  FaCog,
  FaBell,
  FaHome,
  FaUpload
} from 'react-icons/fa';

const ContentDetail = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [contentId, setContentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedContent, setRelatedContent] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTokenValid, setIsTokenValid] = useState(true);
  const [tokenData, setTokenData] = useState(null);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  // Handle window resize for responsiveness
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const translations = {
    'Content Details': 'تفاصيل المحتوى',
    'Loading...': 'جاري التحميل...',
    'Content not found': 'المحتوى غير موجود',
    'Invalid or expired token': 'الرمز غير صالح أو منتهي الصلاحية',
    'Back to Home': 'العودة للرئيسية',
    'Back': 'رجوع',
    'Published on': 'نشر في',
    'Author': 'المؤلف',
    'Category': 'التصنيف',
    'Type': 'النوع',
    'Tags': 'الكلمات الدلالية',
    'Description': 'الوصف',
    'Watch Video': 'مشاهدة الفيديو',
    'Listen to Audio': 'الاستماع للصوت',
    'View Image': 'مشاهدة الصورة',
    'Breaking News': 'خبر عاجل',
    'Featured Content': 'محتوى مميز',
    'Share this content': 'مشاركة هذا المحتوى',
    'Copy Link': 'نسخ الرابط',
    'Link copied!': 'تم نسخ الرابط!',
    'Related Content': 'محتويات ذات صلة',
    'No related content': 'لا يوجد محتويات ذات صلة',
    'Close': 'إغلاق',
    'Preview': 'معاينة',
    'Media Gallery': 'معرض الوسائط',
    'Featured Media': 'وسائط مميزة',
    'Additional Media': 'وسائط إضافية',
    'Documents': 'وثائق',
    'Share': 'مشاركة',
    'Token Information': 'معلومات الرمز',
    'Token Status': 'حالة الرمز',
    'Active': 'نشط',
    'Expired': 'منتهي',
    'Expires at': 'ينتهي في',
    'Uses Left': 'عدد الاستخدامات المتبقية',
    'Max Uses': 'الحد الأقصى للاستخدامات',
    'Private Content': 'محتوى خاص',
    'Access Restricted': 'الوصول مقيد',
    'Media access is restricted': 'الوصول إلى الوسائط مقيد',
    'Please contact administrator': 'يرجى التواصل مع المسؤول',
    'Views': 'مشاهدات',
    'Likes': 'إعجابات',
    'Comments': 'تعليقات',
    'Share this': 'مشاركة',
    'Copy link': 'نسخ الرابط',
    'Statistics': 'الإحصائيات'
  };

  const translateText = (text) => {
    return translations[text] || text;
  };

  // Add showToast function here
  const showToast = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
    setTimeout(() => {
      setToast({ ...toast, show: false });
    }, 3000);
  };

  // Show access restricted message
  const showAccessRestricted = () => {
    showToast(translateText('Media access is restricted'), 'warning');
  };

  const getContentTypeIcon = (type) => {
    const icons = {
      'video': <FaVideo size={16} />,
      'image': <FaImage size={16} />,
      'audio': <FaMusic size={16} />,
      'document': <FaFileAlt size={16} />,
      'news': <FaNewspaper size={16} />,
      'report': <FaChartBar size={16} />,
      'statement': <FaBullhorn size={16} />,
      'article': <FaPenAlt size={16} />,
      'default': <FaPaperclip size={16} />
    };
    return icons[type] || icons.default;
  };

  const getContentTypeText = (type) => {
    const types = {
      'video': 'فيديو',
      'image': 'صورة',
      'audio': 'صوتي',
      'document': 'وثيقة',
      'news': 'خبر',
      'report': 'تقرير',
      'statement': 'بيان',
      'article': 'مقال',
      'default': 'محتوى'
    };
    return types[type] || types.default;
  };

  const getContentTypeColor = (type) => {
    const colors = {
      'video': '#3b82f6',
      'image': '#10b981',
      'audio': '#8b5cf6',
      'document': '#f59e0b',
      'news': '#ef4444',
      'report': '#6366f1',
      'statement': '#ec4899',
      'article': '#14b8a6',
      'default': '#6b7280'
    };
    return colors[type] || colors.default;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return formatDistanceToNow(new Date(dateString), {
        addSuffix: true,
        locale: ar
      });
    } catch (error) {
      return new Date(dateString).toLocaleDateString('ar-SA');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 ب';
    const sizes = ['ب', 'كيلوبايت', 'ميجابايت', 'جيجابايت'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i)) + ' ' + sizes[i];
  };

  // Generate Cloudinary thumbnail URL for video
  const generateCloudinaryThumbnail = (videoUrl, options = {}) => {
    if (!videoUrl) return null;
    
    try {
      const url = new URL(videoUrl);
      const pathParts = url.pathname.split('/');
      const uploadIndex = pathParts.indexOf('upload');
      
      if (uploadIndex === -1) return videoUrl;
      
      const defaultOptions = {
        width: 400,
        height: 225,
        crop: 'fill',
        quality: 'auto:good',
        format: 'jpg'
      };
      
      const thumbOptions = { ...defaultOptions, ...options };
      
      const transformations = [
        `c_${thumbOptions.crop}`,
        `w_${thumbOptions.width}`,
        `h_${thumbOptions.height}`,
        `q_${thumbOptions.quality}`,
        `f_${thumbOptions.format}`
      ].join(',');
      
      return videoUrl.replace('/upload/', `/upload/${transformations}/`);
    } catch (error) {
      return videoUrl;
    }
  };

  // Get thumbnail URL for different content types
  const getThumbnailUrl = (item) => {
    if (!item.featured_media) return null;
    
    if (item.content_type === 'video' && item.featured_media.includes('cloudinary.com')) {
      return generateCloudinaryThumbnail(item.featured_media);
    }
    
    if (item.content_type === 'image') {
      return item.featured_media;
    }
    
    return null;
  };

  // Get fallback icon for content type
  const getFallbackIcon = (contentType) => {
    switch (contentType) {
      case 'video':
        return '🎬';
      case 'image':
        return '🖼️';
      case 'audio':
        return '🎵';
      case 'document':
        return '📄';
      default:
        return '📎';
    }
  };

  // Get background gradient based on content type
  const getContentTypeGradient = (contentType) => {
    switch (contentType) {
      case 'video':
        return 'linear-gradient(135deg, #3b82f6, #1d4ed8)';
      case 'image':
        return 'linear-gradient(135deg, #10b981, #047857)';
      case 'audio':
        return 'linear-gradient(135deg, #8b5cf6, #7c3aed)';
      case 'document':
        return 'linear-gradient(135deg, #f59e0b, #d97706)';
      case 'news':
        return 'linear-gradient(135deg, #ef4444, #b91c1c)';
      case 'report':
        return 'linear-gradient(135deg, #6366f1, #4f46e5)';
      case 'statement':
        return 'linear-gradient(135deg, #ec4899, #be185d)';
      case 'article':
        return 'linear-gradient(135deg, #14b8a6, #0f766e)';
      default:
        return 'linear-gradient(135deg, #6366f1, #4f46e5)';
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href)
      .then(() => {
        showToast(translateText('Link copied!'));
      })
      .catch(err => {
        console.error('Failed to copy:', err);
        showToast('فشل نسخ الرابط', 'danger');
      });
  };

  const shareContent = () => {
    if (!content) return;
    
    if (navigator.share) {
      navigator.share({
        title: content.title,
        text: content.description,
        url: window.location.href,
      }).catch(err => {
        console.error('Error sharing:', err);
        copyToClipboard();
      });
    } else {
      copyToClipboard();
    }
  };

  const handleLike = async () => {
    if (!content) return;
    
    try {
      if (liked) {
        setLikeCount(prev => prev - 1);
        setLiked(false);
        await supabase
          .from('content')
          .update({ likes: (content.likes || 0) - 1 })
          .eq('id', content.id);
      } else {
        setLikeCount(prev => prev + 1);
        setLiked(true);
        await supabase
          .from('content')
          .update({ likes: (content.likes || 0) + 1 })
          .eq('id', content.id);
      }
    } catch (error) {
      console.error('Error updating like:', error);
    }
  };

  // Fetch token data from content_tokens table
  const fetchTokenData = async () => {
    try {
      const { data, error } = await supabase
        .from('content_tokens')
        .select('*')
        .eq('token', token)
        .single();

      if (error) throw error;

      if (data) {
        setTokenData(data);
        
        // Check if token is valid
        const now = new Date();
        const expiresAt = data.expires_at ? new Date(data.expires_at) : null;
        const isExpired = expiresAt && expiresAt < now;
        const hasUsesLeft = !data.max_uses || (data.uses_count || 0) < data.max_uses;
        
        if (isExpired || !hasUsesLeft) {
          setIsTokenValid(false);
          setError(translateText('Invalid or expired token'));
          return null;
        } else {
          setIsTokenValid(true);
          // Increment token usage
          await supabase
            .from('content_tokens')
            .update({ 
              uses_count: (data.uses_count || 0) + 1,
              last_accessed: new Date().toISOString()
            })
            .eq('id', data.id);
          
          return data.content_id;
        }
      } else {
        setIsTokenValid(false);
        setError(translateText('Invalid or expired token'));
        return null;
      }
    } catch (error) {
      console.error('Error fetching token:', error);
      setIsTokenValid(false);
      setError(translateText('Invalid or expired token'));
      return null;
    }
  };

  // Fetch content by ID
  const fetchContentById = async (id) => {
    try {
      const { data, error } = await supabase
        .from('content')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      if (data) {
        console.log('Fetched content:', data);
        setContent(data);
        setContentId(data.id);
        setLikeCount(data.likes || 0);
        
        // Increment views
        await supabase
          .from('content')
          .update({ views: (data.views || 0) + 1 })
          .eq('id', data.id);
        
        // Fetch related content
        fetchRelatedContent(data.category, data.content_type, data.id);
        
        return data;
      } else {
        setError(translateText('Content not found'));
        return null;
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      setError(translateText('Content not found'));
      return null;
    }
  };

  const fetchContent = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching content for token:', token);
      
      // First, fetch token data to get content ID
      const contentIdFromToken = await fetchTokenData();
      
      if (contentIdFromToken && isTokenValid) {
        console.log('Token valid, fetching content ID:', contentIdFromToken);
        
        // Then fetch the actual content
        await fetchContentById(contentIdFromToken);
      } else {
        setError(translateText('Invalid or expired token'));
      }
    } catch (error) {
      console.error('Error in fetchContent:', error);
      setError(translateText('Content not found'));
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedContent = async (category, contentType, excludeId) => {
    try {
      const { data } = await supabase
        .from('content')
        .select('*')
        .eq('status', 'published')
        .neq('id', excludeId)
        .order('created_at', { ascending: false })
        .limit(4);

      if (data) {
        // Fetch tokens for related content
        const relatedWithTokens = await fetchTokensForRelatedContent(data);
        setRelatedContent(relatedWithTokens);
      }
    } catch (error) {
      console.error('Error fetching related content:', error);
    }
  };

  // Fetch tokens for related content
  const fetchTokensForRelatedContent = async (contentItems) => {
    try {
      const contentIds = contentItems.map(item => item.id);
      
      const { data: tokensData, error } = await supabase
        .from('content_tokens')
        .select('token, content_id')
        .in('content_id', contentIds);

      if (error) throw error;

      // Create a map of content_id to token
      const tokenMap = {};
      if (tokensData) {
        tokensData.forEach(token => {
          tokenMap[token.content_id] = token.token;
        });
      }

      // Combine content with tokens
      return contentItems.map(item => ({
        ...item,
        content_token: tokenMap[item.id] || null
      }));
    } catch (error) {
      console.error('Error fetching tokens for related content:', error);
      return contentItems.map(item => ({
        ...item,
        content_token: null
      }));
    }
  };

  const handleMediaLoaded = () => {
    setIsPlayerReady(true);
  };

  const handleMediaError = (e) => {
    console.error('Media error:', e);
    setIsPlayerReady(true);
  };

  const getVideoMimeType = (url) => {
    if (url.includes('.mp4')) return 'video/mp4';
    if (url.includes('.webm')) return 'video/webm';
    if (url.includes('.ogg') || url.includes('.ogv')) return 'video/ogg';
    if (url.includes('.mov')) return 'video/quicktime';
    if (url.includes('.avi')) return 'video/x-msvideo';
    return 'video/mp4';
  };

  const getAudioMimeType = (url) => {
    if (url.includes('.mp3')) return 'audio/mpeg';
    if (url.includes('.wav')) return 'audio/wav';
    if (url.includes('.ogg')) return 'audio/ogg';
    if (url.includes('.m4a')) return 'audio/mp4';
    if (url.includes('.flac')) return 'audio/flac';
    return 'audio/mpeg';
  };

  // Auto-play video function
  const autoPlayVideo = () => {
    if (videoRef.current && content?.content_type === 'video') {
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.log('Auto-play prevented:', error);
        });
      }
    }
  };

  // Auto-play audio function
  const autoPlayAudio = () => {
    if (audioRef.current && content?.content_type === 'audio') {
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.log('Auto-play prevented:', error);
        });
      }
    }
  };

  // Handle auto-play when content is loaded
  useEffect(() => {
    if (content) {
      const timer = setTimeout(() => {
        if (content.content_type === 'video') {
          autoPlayVideo();
        } else if (content.content_type === 'audio') {
          autoPlayAudio();
        }
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [content]);

  useEffect(() => {
    if (token) {
      fetchContent();
    }
  }, [token]);

  const renderMediaPlayer = () => {
    if (!content) return null;
    
    const mediaUrl = content.featured_media || content.featured_media_data?.url;
    if (!mediaUrl) return null;

    const getMediaSectionTitle = () => {
      switch (content.content_type) {
        case 'video':
          return 'مشاهدة الفيديو';
        case 'audio':
          return 'الاستماع للصوت';
        case 'image':
          return 'مشاهدة الصورة';
        default:
          return 'مشاهدة الوسائط';
      }
    };

    return (
      <div className="media-player-section mb-4">
        <h3 className="media-section-title">
          {getMediaSectionTitle()}
        </h3>
        
        <div className="media-player-container">
          {content.content_type === 'video' ? (
            <div className="video-player-wrapper">
              {!isPlayerReady && (
                <div className="player-loading">
                  <Spinner animation="border" variant="light" size="sm" />
                  <span>جاري تحميل الفيديو...</span>
                </div>
              )}
              <video
                ref={videoRef}
                controls
                autoPlay
                muted={content.content_type === 'video'}
                playsInline
                className="video-player"
                onLoadedData={handleMediaLoaded}
                onCanPlay={handleMediaLoaded}
                onError={handleMediaError}
                preload="auto"
              >
                <source src={mediaUrl} type={getVideoMimeType(mediaUrl)} />
                <p>متصفحك لا يدعم تشغيل الفيديو. يرجى تحديث متصفحك أو تحميل الفيديو.</p>
              </video>
            </div>
          ) : content.content_type === 'audio' ? (
            <div className="audio-player-wrapper">
              {!isPlayerReady && (
                <div className="player-loading">
                  <Spinner animation="border" variant="primary" size="sm" />
                  <span>جاري تحميل الصوت...</span>
                </div>
              )}
              <audio
                ref={audioRef}
                controls
                autoPlay
                className="audio-player"
                onLoadedData={handleMediaLoaded}
                onCanPlay={handleMediaLoaded}
                onError={handleMediaError}
                preload="auto"
              >
                <source src={mediaUrl} type={getAudioMimeType(mediaUrl)} />
                <p>متصفحك لا يدعم تشغيل الصوت. يرجى تحديث متصفحك أو تحميل الملف.</p>
              </audio>
            </div>
          ) : content.content_type === 'image' ? (
            <div className="image-viewer-wrapper">
              <img
                src={mediaUrl}
                alt={content.title}
                className="image-viewer"
                onLoad={handleMediaLoaded}
                onError={handleMediaError}
              />
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const renderMediaGallery = () => {
    if (!content) return null;

    const featuredMedia = content.featured_media || content.featured_media_data?.url;
    const additionalMedia = content.additional_media || [];
    const documents = content.documents || [];

    const showFeaturedInGallery = content.content_type !== 'video' && 
                                  content.content_type !== 'audio' && 
                                  content.content_type !== 'image';

    return (
      <div className="media-gallery">
        {showFeaturedInGallery && featuredMedia && (
          <div className="media-section mb-4">
            <h5 className="media-section-title">
              <FaStar size={16} />
              {translateText('Featured Media')}
            </h5>
            <div className="media-preview">
              <div className="media-preview-inner">
                <div className="media-icon">
                  <FaImage size={20} />
                </div>
                <div className="media-info">
                  <div className="media-filename">
                    {content.title}
                    <Badge bg="primary" className="featured-badge">
                      مميز
                    </Badge>
                  </div>
                  <div className="media-details">
                    وسائط مميزة
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    showAccessRestricted();
                  }}
                  className="preview-button"
                >
                  <FaExternalLinkAlt size={14} /> مشاهدة
                </button>
              </div>
            </div>
          </div>
        )}

        {Array.isArray(additionalMedia) && additionalMedia.length > 0 && (
          <div className="media-section mb-4">
            <h5 className="media-section-title">
              <FaImages size={16} />
              {translateText('Additional Media')}
            </h5>
            {additionalMedia.map((media, index) => {
              const mediaUrl = typeof media === 'string' ? media : (media?.url || media);
              const mediaData = typeof media === 'object' ? media : null;
              const fileName = mediaData?.original_filename || 
                               mediaUrl?.split('/').pop() || 
                               `وسائط_${index + 1}`;

              return (
                <div key={index} className="media-preview">
                  <div className="media-preview-inner">
                    <div className="media-icon">
                      <FaImage size={20} />
                    </div>
                    <div className="media-info">
                      <div className="media-filename">
                        {fileName}
                      </div>
                      <div className="media-details">
                        وسائط إضافية
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        showAccessRestricted();
                      }}
                      className="preview-button"
                    >
                      <FaExternalLinkAlt size={14} /> مشاهدة
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {Array.isArray(documents) && documents.length > 0 && (
          <div className="media-section">
            <h5 className="media-section-title">
              <FaFile size={16} />
              {translateText('Documents')}
            </h5>
            {documents.map((doc, index) => {
              const docUrl = typeof doc === 'string' ? doc : (doc?.url || doc);
              const docData = typeof doc === 'object' ? doc : null;
              const fileName = docData?.original_filename || 
                               docUrl?.split('/').pop() || 
                               `وثيقة_${index + 1}`;
              const fileSize = docData?.size ? formatFileSize(docData.size) : '';

              return (
                <div key={index} className="media-preview">
                  <div className="media-preview-inner">
                    <div className="media-icon">
                      <FaFileAlt size={20} />
                    </div>
                    <div className="media-info">
                      <div className="media-filename">
                        {fileName}
                      </div>
                      <div className="media-details">
                        وثيقة {fileSize && `• ${fileSize}`}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        showAccessRestricted();
                      }}
                      className="preview-button"
                    >
                      <FaExternalLinkAlt size={14} /> مشاهدة
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderRelatedContent = () => {
    if (!relatedContent.length) return null;

    return (
      <div className="related-section">
        <h3 className="related-title">
          <FaShareSquare size={20} />
          {translateText('Related Content')}
        </h3>
        <div className="related-grid">
          {relatedContent.map((item) => {
            const thumbnailUrl = getThumbnailUrl(item);
            const isVideo = item.content_type === 'video';
            const gradientBg = getContentTypeGradient(item.content_type);
            const contentTypeColor = getContentTypeColor(item.content_type);
            
            return (
              <Link 
                key={item.id} 
                to={`/content/${item.content_token || item.id}`}
                className="related-card"
              >
                <div className="related-image-container" style={{ 
                  background: gradientBg
                }}>
                  {thumbnailUrl ? (
                    <>
                      <img 
                        src={thumbnailUrl} 
                        alt={item.title_ar || item.title}
                        className="related-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `
                            <div class="related-fallback-icon" style="background: ${gradientBg}">
                              ${getFallbackIcon(item.content_type)}
                            </div>
                          `;
                        }}
                      />
                      {isVideo && (
                        <div className="related-video-overlay">
                          <FaPlay size={24} />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="related-fallback-icon">
                      {getFallbackIcon(item.content_type)}
                    </div>
                  )}
                  
                  <Badge className="related-type-badge" style={{ backgroundColor: contentTypeColor }}>
                    {getContentTypeIcon(item.content_type)}
                    <span>{getContentTypeText(item.content_type)}</span>
                  </Badge>
                  
                  {item.is_breaking && (
                    <Badge className="related-breaking-badge">
                      <FaExclamationCircle size={10} />
                      <span>عاجل</span>
                    </Badge>
                  )}
                </div>
                
                <div className="related-content">
                  <h5 className="related-card-title">
                    {item.title_ar || item.title}
                  </h5>
                  <div className="related-meta">
                    <span className="related-meta-item">
                      <FaCalendarAlt size={12} />
                      {formatDate(item.created_at)}
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    );
  };

  // Add CSS styles
  const styles = `
    /* App Layout */
    .app {
      display: flex;
      min-height: 100vh;
      background: #0a0a0a;
      font-family: 'ITF Qomra Arabic', 'Segoe UI', sans-serif;
      direction: rtl;
    }

    /* Sidebar */
    .sidebar {
      width: 280px;
      background: #111111;
      border-left: 1px solid rgba(255, 255, 255, 0.05);
      padding: 2rem 1.5rem;
      position: fixed;
      right: 0;
      top: 0;
      bottom: 0;
      overflow-y: auto;
      transition: transform 0.3s ease;
      z-index: 1000;
    }

    .sidebar.closed {
      transform: translateX(280px);
    }

    .sidebar-header {
      display: flex;
      align-items: center;
      gap: 1rem;
      margin-bottom: 2.5rem;
      padding-bottom: 1.5rem;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    }

    .sidebar-logo {
      width: 40px;
      height: 40px;
      background: linear-gradient(135deg, #6366f1, #8b5cf6);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      font-size: 1.2rem;
      color: #ffffff;
    }

    .sidebar-title {
      font-size: 1.2rem;
      font-weight: 600;
      color: #ffffff;
    }

    .sidebar-nav {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      margin-bottom: 2rem;
    }

    .nav-item {
      display: flex;
      align-items: center;
      gap: 1rem;
      padding: 0.75rem 1rem;
      border-radius: 12px;
      color: #a3a3a3;
      text-decoration: none;
      transition: all 0.2s ease;
      cursor: pointer;
    }

    .nav-item:hover {
      background: rgba(255, 255, 255, 0.05);
      color: #ffffff;
    }

    .nav-item.active {
      background: rgba(99, 102, 241, 0.1);
      color: #818cf8;
    }

    .nav-icon {
      font-size: 1.2rem;
    }

    /* Main Content */
    .main-content {
      flex: 1;
      margin-right: 280px;
      transition: margin-right 0.3s ease;
      min-width: 0;
    }

    .main-content.sidebar-closed {
      margin-right: 0;
    }

    /* Content Detail Container */
    .content-detail-container {
      padding: 2rem;
      max-width: 1200px;
      margin: 0 auto;
    }

    /* Back Button */
    .back-button {
      margin-bottom: 1.5rem;
    }

    .back-button .btn {
      background: #1a1a1a;
      border: 1px solid rgba(255, 255, 255, 0.1);
      color: #a3a3a3;
      border-radius: 40px;
      padding: 0.5rem 1.25rem;
      font-weight: 500;
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      transition: all 0.2s ease;
      cursor: pointer;
      text-decoration: none;
    }

    .back-button .btn:hover {
      background: #2a2a2a;
      color: #ffffff;
      border-color: #6366f1;
    }

    /* Content Header */
    .content-header {
      background: #111111;
      border: 1px solid rgba(255, 255, 255, 0.05);
      border-radius: 16px;
      padding: 2rem;
      margin-bottom: 1.5rem;
    }

    .content-title {
      font-size: 2rem;
      font-weight: 700;
      color: #ffffff;
      margin-bottom: 1rem;
      line-height: 1.4;
    }

    .meta-info {
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
      margin-bottom: 1.5rem;
      align-items: center;
    }

    .meta-badge {
      background: rgba(99, 102, 241, 0.1);
      color: #818cf8;
      padding: 0.5rem 1rem;
      border-radius: 40px;
      font-size: 0.85rem;
      font-weight: 500;
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      border: 1px solid rgba(255, 255, 255, 0.05);
    }

    .meta-badge.type {
      background: linear-gradient(135deg, #6366f1, #8b5cf6);
      color: #ffffff;
    }

    .meta-badge.breaking {
      background: #dc2626;
      color: #ffffff;
    }

    .meta-text {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #a3a3a3;
      font-size: 0.9rem;
    }

    .meta-text svg {
      color: #818cf8;
    }

    /* Action Buttons */
    .action-buttons {
      display: flex;
      gap: 1rem;
      flex-wrap: wrap;
    }

    .action-button {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      padding: 0.75rem 1.5rem;
      border-radius: 40px;
      border: 1px solid rgba(255, 255, 255, 0.1);
      background: #1a1a1a;
      color: #a3a3a3;
      cursor: pointer;
      transition: all 0.2s ease;
      font-size: 0.95rem;
      font-weight: 500;
    }

    .action-button:hover {
      background: #2a2a2a;
      color: #ffffff;
      border-color: #6366f1;
    }

    .action-button.like {
      color: #f43f5e;
    }

    .action-button.like:hover {
      background: rgba(244, 63, 94, 0.1);
      border-color: #f43f5e;
    }

    .action-button.like.liked {
      background: #f43f5e;
      color: #ffffff;
      border-color: #f43f5e;
    }

    .action-button.share {
      color: #3b82f6;
    }

    .action-button.share:hover {
      background: rgba(59, 130, 246, 0.1);
      border-color: #3b82f6;
    }

    /* Media Player Section */
    .media-player-section {
      background: #111111;
      border: 1px solid rgba(255, 255, 255, 0.05);
      border-radius: 16px;
      padding: 1.5rem;
      margin-bottom: 1.5rem;
    }

    .media-section-title {
      font-size: 1.2rem;
      font-weight: 600;
      color: #ffffff;
      margin-bottom: 1rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .media-section-title svg {
      color: #818cf8;
    }

    .media-player-container {
      background: #000000;
      border-radius: 12px;
      overflow: hidden;
      margin-bottom: 1rem;
    }

    .video-player-wrapper {
      position: relative;
      padding-top: 56.25%;
    }

    .player-loading {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #000000;
      color: #ffffff;
      z-index: 1;
      gap: 1rem;
    }

    .video-player {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: #000000;
    }

    .audio-player-wrapper {
      padding: 2rem;
      background: #1a1a1a;
    }

    .audio-player {
      width: 100%;
      height: 40px;
    }

    .image-viewer-wrapper {
      text-align: center;
      padding: 1rem;
    }

    .image-viewer {
      max-width: 100%;
      max-height: 70vh;
      border-radius: 8px;
    }

    /* Media Gallery Section */
    .media-gallery-section {
      background: #111111;
      border: 1px solid rgba(255, 255, 255, 0.05);
      border-radius: 16px;
      padding: 1.5rem;
      margin-bottom: 1.5rem;
    }

    .media-preview {
      background: #1a1a1a;
      border: 1px solid rgba(255, 255, 255, 0.05);
      border-radius: 12px;
      padding: 1rem;
      margin-bottom: 0.75rem;
      transition: all 0.2s ease;
    }

    .media-preview:hover {
      border-color: #6366f1;
      background: #1f1f1f;
    }

    .media-preview-inner {
      display: flex;
      align-items: center;
      gap: 1rem;
      flex-wrap: wrap;
    }

    .media-icon {
      width: 40px;
      height: 40px;
      background: #111111;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #818cf8;
      font-size: 1.2rem;
    }

    .media-info {
      flex: 1;
      min-width: 0;
    }

    .media-filename {
      font-weight: 600;
      color: #ffffff;
      margin-bottom: 0.25rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    .media-details {
      font-size: 0.85rem;
      color: #a3a3a3;
    }

    .preview-button {
      background: none;
      border: 1px solid #6366f1;
      color: #818cf8;
      padding: 0.5rem 1rem;
      border-radius: 8px;
      font-size: 0.85rem;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      transition: all 0.2s ease;
    }

    .preview-button:hover {
      background: #6366f1;
      color: #ffffff;
    }

    .featured-badge {
      background: #818cf8;
      font-size: 0.7rem;
      padding: 0.25rem 0.5rem;
    }

    /* Description Card */
    .description-card {
      background: #111111;
      border: 1px solid rgba(255, 255, 255, 0.05);
      border-radius: 16px;
      padding: 1.5rem;
      margin-bottom: 1.5rem;
    }

    .description-title {
      font-size: 1.2rem;
      font-weight: 600;
      color: #ffffff;
      margin-bottom: 1rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .description-text {
      color: #a3a3a3;
      line-height: 1.8;
      font-size: 1rem;
      white-space: pre-line;
    }

    /* Tags Section */
    .tags-section {
      background: #111111;
      border: 1px solid rgba(255, 255, 255, 0.05);
      border-radius: 16px;
      padding: 1.5rem;
      margin-bottom: 1.5rem;
    }

    .tags-container {
      display: flex;
      flex-wrap: wrap;
      gap: 0.75rem;
    }

    .tag-badge {
      background: #1a1a1a;
      border: 1px solid rgba(255, 255, 255, 0.05);
      color: #a3a3a3;
      padding: 0.5rem 1rem;
      border-radius: 40px;
      font-size: 0.85rem;
      font-weight: 500;
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      transition: all 0.2s ease;
    }

    .tag-badge:hover {
      background: #2a2a2a;
      border-color: #6366f1;
      color: #818cf8;
    }

    /* Token Info Section */
    .token-info-section {
      background: #111111;
      border: 1px solid rgba(255, 255, 255, 0.05);
      border-radius: 16px;
      padding: 1.5rem;
      margin-bottom: 1.5rem;
      border-right: 4px solid #6366f1;
    }

    .token-info-title {
      font-size: 1.1rem;
      font-weight: 600;
      color: #ffffff;
      margin-bottom: 1rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .token-info-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1rem;
    }

    .token-info-item {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    .token-info-label {
      font-size: 0.85rem;
      color: #a3a3a3;
    }

    .token-info-value {
      font-size: 1rem;
      color: #ffffff;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .token-status-active {
      color: #10b981;
    }

    .token-status-expired {
      color: #ef4444;
    }

    /* Related Section */
    .related-section {
      margin-top: 2rem;
    }

    .related-title {
      font-size: 1.5rem;
      font-weight: 700;
      color: #ffffff;
      margin-bottom: 1.5rem;
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    .related-title svg {
      color: #818cf8;
    }

    .related-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 1.5rem;
    }

    .related-card {
      background: #111111;
      border: 1px solid rgba(255, 255, 255, 0.05);
      border-radius: 16px;
      overflow: hidden;
      transition: all 0.3s ease;
      text-decoration: none;
      color: inherit;
    }

    .related-card:hover {
      transform: translateY(-4px);
      border-color: #6366f1;
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.5);
    }

    .related-image-container {
      position: relative;
      height: 160px;
      overflow: hidden;
    }

    .related-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.5s ease;
    }

    .related-card:hover .related-image {
      transform: scale(1.05);
    }

    .related-fallback-icon {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #ffffff;
      font-size: 2rem;
    }

    .related-video-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      color: #ffffff;
    }

    .related-type-badge {
      position: absolute;
      top: 0.75rem;
      right: 0.75rem;
      background: rgba(0, 0, 0, 0.8);
      color: #ffffff;
      padding: 0.25rem 0.5rem;
      border-radius: 6px;
      font-size: 0.7rem;
      display: flex;
      align-items: center;
      gap: 0.25rem;
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .related-breaking-badge {
      position: absolute;
      top: 0.75rem;
      left: 0.75rem;
      background: #dc2626;
      color: #ffffff;
      padding: 0.25rem 0.5rem;
      border-radius: 6px;
      font-size: 0.7rem;
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }

    .related-content {
      padding: 1rem;
    }

    .related-card-title {
      font-size: 1rem;
      font-weight: 600;
      color: #ffffff;
      margin-bottom: 0.5rem;
      line-height: 1.4;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .related-meta {
      display: flex;
      gap: 1rem;
      color: #a3a3a3;
      font-size: 0.8rem;
    }

    .related-meta-item {
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }

    /* Loading Container */
    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 400px;
    }

    .loading-spinner {
      width: 50px;
      height: 50px;
      border: 3px solid rgba(99, 102, 241, 0.1);
      border-top-color: #6366f1;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-bottom: 1rem;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    /* Empty State */
    .empty-state {
      text-align: center;
      padding: 3rem;
      background: #111111;
      border: 1px solid rgba(255, 255, 255, 0.05);
      border-radius: 16px;
    }

    .empty-icon {
      font-size: 3rem;
      color: #404040;
      margin-bottom: 1rem;
    }

    .empty-title {
      font-size: 1.2rem;
      font-weight: 600;
      color: #ffffff;
      margin-bottom: 0.5rem;
    }

    .empty-text {
      color: #a3a3a3;
    }

    /* Toast Notifications */
    .toast-container {
      position: fixed;
      top: 1rem;
      left: 1rem;
      z-index: 9999;
    }

    .toast {
      background: #1a1a1a;
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 8px;
      color: #ffffff;
    }

    .toast.success {
      border-right: 4px solid #10b981;
    }

    .toast.error {
      border-right: 4px solid #ef4444;
    }

    .toast.warning {
      border-right: 4px solid #f59e0b;
    }

    /* Responsive */
    @media (max-width: 992px) {
      .sidebar {
        transform: translateX(280px);
      }
      
      .sidebar.open {
        transform: translateX(0);
      }
      
      .main-content {
        margin-right: 0;
      }
      
      .menu-toggle {
        display: block;
      }

      .stats-grid {
        grid-template-columns: repeat(2, 1fr);
      }
    }

    @media (max-width: 768px) {
      .content-detail-container {
        padding: 1rem;
      }

      .content-header {
        padding: 1.5rem;
      }

      .content-title {
        font-size: 1.5rem;
      }

      .meta-info {
        flex-direction: column;
        align-items: flex-start;
      }

      .action-buttons {
        flex-direction: column;
      }

      .action-button {
        width: 100%;
      }

      .related-grid {
        grid-template-columns: 1fr;
      }

      .stats-grid {
        grid-template-columns: 1fr;
      }

      .token-info-grid {
        grid-template-columns: 1fr;
      }

      .media-preview-inner {
        flex-direction: column;
        align-items: flex-start;
      }

      .preview-button {
        width: 100%;
      }
    }
  `;

  if (loading) {
    return (
      <div className="app">
        <style>{styles}</style>
        <div className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
          <div className="sidebar-header">
            <div className="sidebar-logo">إ</div>
            <div className="sidebar-title">إرضاء الإعلامية</div>
          </div>
          <nav className="sidebar-nav">
            <div className="nav-item" onClick={() => navigate('/')}>
              <FaHome className="nav-icon" />
              <span>الرئيسية</span>
            </div>
            <div className="nav-item">
              <FaUpload className="nav-icon" />
              <span>رفع محتوى</span>
            </div>
            <div className="nav-item">
              <FaFileAlt className="nav-icon" />
              <span>محتواي</span>
            </div>
            <div className="nav-item">
              <FaCog className="nav-icon" />
              <span>الإعدادات</span>
            </div>
          </nav>
        </div>

        <div className={`main-content ${!sidebarOpen ? 'sidebar-closed' : ''}`}>
          <div className="content-detail-container">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p style={{ color: '#a3a3a3' }}>{translateText('Loading...')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="app">
        <style>{styles}</style>
        <div className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
          <div className="sidebar-header">
            <div className="sidebar-logo">إ</div>
            <div className="sidebar-title">إرضاء الإعلامية</div>
          </div>
          <nav className="sidebar-nav">
            <div className="nav-item" onClick={() => navigate('/')}>
              <FaHome className="nav-icon" />
              <span>الرئيسية</span>
            </div>
            <div className="nav-item">
              <FaUpload className="nav-icon" />
              <span>رفع محتوى</span>
            </div>
          </nav>
        </div>

        <div className={`main-content ${!sidebarOpen ? 'sidebar-closed' : ''}`}>
          <div className="content-detail-container">
            <div className="empty-state">
              <div className="empty-icon">
                <FaExclamationCircle />
              </div>
              <h3 className="empty-title">{error || translateText('Content not found')}</h3>
              <Button 
                variant="primary" 
                onClick={() => navigate('/')} 
                className="mt-3"
                style={{ borderRadius: '40px', padding: '0.75rem 2rem' }}
              >
                {translateText('Back to Home')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <style>{styles}</style>

      <Helmet>
        <title>{content.title} - مؤسسة إرضاء الإعلامية</title>
        <meta name="description" content={content.description} />
        <meta property="og:title" content={content.title} />
        <meta property="og:description" content={content.description} />
        <meta property="og:image" content={content.featured_media} />
        <meta property="og:url" content={window.location.href} />
      </Helmet>

      {/* Toast Notifications */}
      <ToastContainer className="toast-container">
        <Toast 
          show={toast.show} 
          onClose={() => setToast({ ...toast, show: false })}
          delay={3000} 
          autohide
          className={`toast ${toast.variant}`}
        >
          <Toast.Body style={{ 
            fontFamily: "'ITF Qomra Arabic', sans-serif",
            textAlign: 'right'
          }}>
            {toast.message}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">إ</div>
          <div className="sidebar-title">إرضاء الإعلامية</div>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-item" onClick={() => navigate('/')}>
            <FaHome className="nav-icon" />
            <span>الرئيسية</span>
          </div>
          <div className="nav-item">
            <FaUpload className="nav-icon" />
            <span>رفع محتوى</span>
          </div>
          <div className="nav-item">
            <FaFileAlt className="nav-icon" />
            <span>محتواي</span>
          </div>
          <div className="nav-item">
            <FaCog className="nav-icon" />
            <span>الإعدادات</span>
          </div>
        </nav>
      </div>

      {/* Main Content */}
      <div className={`main-content ${!sidebarOpen ? 'sidebar-closed' : ''}`}>
        {/* Content Detail */}
        <div className="content-detail-container">
          {/* Back Button */}
          <div className="back-button">
            <button
              onClick={() => navigate(-1)}
              className="btn"
            >
              <FaArrowLeft size={14} /> {translateText('Back')}
            </button>
          </div>

          {/* Token Info (if exists) */}
          {tokenData && (
            <div className="token-info-section">
              <h4 className="token-info-title">
                <FaEye />
                {translateText('Token Information')}
              </h4>
              <div className="token-info-grid">
                <div className="token-info-item">
                  <span className="token-info-label">{translateText('Token Status')}</span>
                  <span className="token-info-value">
                    <span className={isTokenValid ? 'token-status-active' : 'token-status-expired'}>
                      {isTokenValid ? '●' : '○'}
                    </span>
                    {isTokenValid ? translateText('Active') : translateText('Expired')}
                  </span>
                </div>
                {tokenData.expires_at && (
                  <div className="token-info-item">
                    <span className="token-info-label">{translateText('Expires at')}</span>
                    <span className="token-info-value">
                      {new Date(tokenData.expires_at).toLocaleDateString('ar-SA')}
                    </span>
                  </div>
                )}
                {tokenData.max_uses && (
                  <div className="token-info-item">
                    <span className="token-info-label">{translateText('Uses Left')}</span>
                    <span className="token-info-value">
                      {Math.max(0, tokenData.max_uses - (tokenData.uses_count || 0))} / {tokenData.max_uses}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Content Header */}
          <div className="content-header">
            <h1 className="content-title">
              {content.title_ar || content.title}
            </h1>
            
            <div className="meta-info">
              <span className="meta-badge type">
                {getContentTypeIcon(content.content_type)}
                {getContentTypeText(content.content_type)}
              </span>
              
              <span className="meta-badge">
                <FaTag size={14} />
                {content.category}
              </span>
              
              {content.is_breaking && (
                <span className="meta-badge breaking">
                  ⚡ {translateText('Breaking News')}
                </span>
              )}
              
              <span className="meta-text">
                <FaUser size={14} /> {content.author_ar || content.author}
              </span>
              
              <span className="meta-text">
                <FaCalendarAlt size={14} /> {formatDate(content.created_at)}
              </span>
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
              <button
                onClick={shareContent}
                className="action-button share"
              >
                <FaShareAlt size={16} />
                <span>{translateText('Share')}</span>
              </button>
            </div>
          </div>

          {/* Media Player Section */}
          {renderMediaPlayer()}

          {/* Media Gallery Section */}
          <div className="media-gallery-section">
            <h3 className="media-section-title">
              <FaImage size={16} />
              {translateText('Media Gallery')}
            </h3>
            {renderMediaGallery()}
          </div>

          {/* Description */}
          <div className="description-card">
            <h5 className="description-title">
              <FaFileAlt size={16} />
              {translateText('Description')}
            </h5>
            <div className="description-text">
              {content.description_ar || content.description}
            </div>
          </div>

          {/* Tags */}
          {content.tags && content.tags.length > 0 && (
            <div className="tags-section">
              <h5 className="description-title">
                <FaTag size={16} />
                {translateText('Tags')}
              </h5>
              <div className="tags-container">
                {content.tags.map((tag, index) => (
                  <Badge key={index} className="tag-badge">
                    #{content.tags_ar?.[index] || tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Related Content */}
          {renderRelatedContent()}
        </div>
      </div>
    </div>
  );
};

export default ContentDetail;